import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import '../core/App.css';

import type CamlinSocketHandler from '../channels/voice/CamlinSocketHandler';
import { handleEvent } from '../channels/voice/voiceHandlers';
import { AudioPermissionChip } from '../core/components/AudioPermissionChip';
import { AvatarAudioLayer } from '../core/components/AvatarAudioLayer';
import { AvatarHealthBadge } from '../core/components/AvatarHealthBadge';
import { LiveControlDock } from '../core/components/LiveControlDock';
import { LiveVoiceOverlay } from '../core/components/LiveVoiceOverlay';
import {
  useAudioPlayback,
  useAvatarConfig,
  useHostedLiveController,
  useLiveListenControls,
  useLiveOverlayState
} from '../core/hooks';
import {
  resolveInitialLiveConnectRequested,
  resolveLinkedAvatarModelId,
  resolveTtsBaseUrl
} from '../core/utils/liveControllerUtils';
import { showToast } from '../core/utils/uiUtils';
import { useVoiceMuteControl } from '../core/hooks/useVoiceMuteControl';
import type { AvatarLiveSessionKind, AvatarSessionContext } from '../session';
import { CamlinAvatar, type CamlinAvatarConfig, type CamlinAvatarHandle } from './CamlinAvatar';
import { resolveSelectedModel } from './resolveSelectedModel';

interface NativeLiveAvatarProps {
  activeListen?: boolean;
  /** Suppress spoken prompt playback until unlockAudio — see useLivePromptPlayback. */
  holdSpeechUntilUnlock?: boolean;
  cameraZoom?: number;
  className?: string;
  diagramId?: string;
  handoffToken?: string;
  isVisualOnly?: boolean;
  liveDestination?: string;
  modelUrl?: string;
  noSip?: boolean;
  onError?: (error: Error) => void;
  onMicClick?: () => void;
  onMoodChange?: (mood: string) => void;
  onReady?: () => void;
  onSpeakingEnd?: () => void;
  onSpeakingStart?: () => void;
  onSpeechInput?: (text: string) => void;
  projectToken?: string;
  requestedAvatarModelId?: string;
  requestedLiveSessionKind?: AvatarLiveSessionKind;
  requestedLiveVoiceId?: string;
  runtimeUrl?: string;
  sessionContext?: Partial<AvatarSessionContext> | AvatarSessionContext | null;
  hideButtons?: boolean;
  /**
   * Opt-out for the always-on AvatarHealthBadge status pip — for embedded
   * surfaces that render their own connection/health UI (e.g. the Camlin
   * Companion stage, where the chip clips inside a 64px launcher circle).
   */
  hideHealthBadge?: boolean;
  /**
   * Opt-out for the SDK's inline AudioPermissionChip overlay. Hosts that
   * render their own labelled permission affordances (Camlin Companion)
   * pass true and consume `onBrowserAccessPromptChange` instead — inside
   * the Companion's minimized launcher the chip rendered as a bare orange
   * icon ON TOP of the avatar's face, and the host's pointer-events:none
   * embed wrapper made it unclickable (Keith, 6 Aug 2026).
   */
  hideAudioPermissionChip?: boolean;
  /**
   * Fires whenever the audio/microphone browser-access prompt state
   * changes (null = no prompt needed). Pairs with
   * `hideAudioPermissionChip` so a host can render its own permission UI
   * and call the handle's `retryBrowserAccess()` from a user gesture.
   */
  onBrowserAccessPromptChange?: (
    prompt: { kind: 'audio' | 'microphone'; title: string; message: string } | null
  ) => void;
  /** See `AvatarEmbedConfig.preloadModel` — false disables the HTTP cache-warming fetch that can race the GLTF loader. */
  preloadModel?: boolean;
  sipWebSocketUrl?: string;
  style?: React.CSSProperties;
  suppressEmbeddedLiveCopy?: boolean;
  handleInputExternally?: boolean;
  ttsUrl?: string;
  useEmbeddedTextures?: boolean;
  voiceUrl?: string;
  initialAppearance?: Record<string, string | undefined>;
  initialConfig?: Record<string, unknown>;
  /**
   * Shared instance id for targeted `avatar:config:update` events —
   * threaded down to CamlinAvatar/Scene AND into the hosted live
   * controller's `linkedAvatarConfig` replay so the runtime's linked
   * config reaches exactly this avatar. When omitted a private id is
   * generated (the linked-config replay is then still targeted, just
   * with an id no other instance shares).
   */
  avatarInstanceId?: string;
  onAppearanceApplied?: () => void;
  /**
   * If `true`, open the visual-channel WebSocket as soon as the avatar
   * becomes ready, without waiting for a user click. See
   * `AvatarHostLiveConfig.autoOpenChannel` for the full rationale.
   * Threaded through from `live.autoOpenChannel` on the embed config.
   */
  autoOpenChannel?: boolean;
}

interface HostedAvatarRef extends CamlinAvatarHandle {
  setIsListening?: (listening: boolean) => void;
  setIsThinking?: (thinking: boolean) => void;
}

type AnimateCallSpeakValue = boolean | { blendData?: any };

// Moved to ./resolveSelectedModel.ts (7 Aug 2026 voice-mismatch round) so the
// voice-pin regression test can import it without this component's CSS
// imports breaking node:test.

export const NativeLiveAvatar = forwardRef<CamlinAvatarHandle, NativeLiveAvatarProps>(
  function NativeLiveAvatar({
    activeListen = false,
    holdSpeechUntilUnlock = false,
    cameraZoom,
    className,
    diagramId,
    handoffToken,
    isVisualOnly = false,
    liveDestination,
    modelUrl,
    noSip = false,
    onError,
    onMicClick,
    onMoodChange,
    onReady,
    onSpeakingEnd,
    onSpeakingStart,
    onSpeechInput,
    projectToken,
    requestedAvatarModelId,
    requestedLiveSessionKind,
    requestedLiveVoiceId,
    runtimeUrl,
    sessionContext,
    hideButtons = false,
    hideHealthBadge = false,
    hideAudioPermissionChip = false,
    onBrowserAccessPromptChange,
    preloadModel,
    sipWebSocketUrl,
    style,
    suppressEmbeddedLiveCopy: suppressEmbeddedLiveCopyOverride = false,
    handleInputExternally = false,
    ttsUrl,
    useEmbeddedTextures = false,
    voiceUrl,
    initialAppearance,
    initialConfig,
    avatarInstanceId,
    onAppearanceApplied,
    autoOpenChannel = false
  }, ref) {
    const generatedInstanceIdRef = useRef(`camlin-live-avatar-${Math.random().toString(36).slice(2)}`);
    const effectiveAvatarInstanceId = avatarInstanceId ?? generatedInstanceIdRef.current;
    const avatarRef = useRef<HostedAvatarRef | null>(null);
    const canvasContainerRef = useRef<HTMLDivElement | null>(null);
    const socketHandlerRef = useRef<CamlinSocketHandler | null>(null);
    const isCallActiveRef = useRef(false);
    const readyNotifiedRef = useRef(false);
    const lastTranscriptEventIdRef = useRef(0);
    const lastErrorRef = useRef<string | null>(null);

    const {
      audioSource,
      setAudioSource,
      isPlaying: playing,
      setPlaying,
      audioPlayerRef: audioPlayer,
      unlockAudio,
      stopAudio: stopAudioPlayback
    } = useAudioPlayback();

    const [record, setRecord] = useState(false);
    const [, setSpeak] = useState(false);
    const [, setAnimateCallSpeak] = useState<AnimateCallSpeakValue>(false);
    const [, setDisplayText] = useState('Ready');
    const [, setUserTalking] = useState(false);
    const [isCallActive, setIsCallActive] = useState(false);
    const [isWebrtcCall, setIsWebrtcCall] = useState(false);
    const [isMobileCall, setIsMobileCall] = useState(false);
    const [lastHangupTime, setLastHangupTime] = useState(0);

    useEffect(() => {
      isCallActiveRef.current = isCallActive;
    }, [isCallActive]);

    const projectTokenCamlin = sessionContext?.projectToken ?? projectToken ?? '';
    const handoffTokenCamlin = sessionContext?.handoffToken ?? handoffToken ?? '';
    const builderDiagramId = sessionContext?.diagramId ?? diagramId ?? (
      requestedLiveSessionKind === 'contextual-chat' ? '' : 'main'
    );

    const config = useAvatarConfig({
      activeListen,
      builderDiagramId,
      cameraZoom,
      forceLiveMode: true,
      handoffToken: handoffTokenCamlin,
      isVisualOnly,
      liveDestination,
      noSip,
      projectToken: projectTokenCamlin,
      runtimeUrl,
      sdkMode: true,
      sipWebSocketUrl,
      ttsUrl,
      voiceUrl
    });

    const selectedModel = useMemo(() => resolveSelectedModel({
      modelUrl,
      requestedAvatarModelId,
      requestedLiveVoiceId,
      useEmbeddedTextures
    }), [
      modelUrl,
      requestedAvatarModelId,
      requestedLiveVoiceId,
      useEmbeddedTextures
    ]);

    const queryParams = useMemo(() => {
      const params = new URLSearchParams();

      if (sessionContext?.sessionId) {
        params.set('sessionId', sessionContext.sessionId);
      }
      if (sessionContext?.userId) {
        params.set('userId', sessionContext.userId);
      }

      return params;
    }, [sessionContext?.sessionId, sessionContext?.userId]);

    const resolvedTtsBaseUrl = useMemo(
      () => resolveTtsBaseUrl(config.ttsUrl),
      [config.ttsUrl]
    );
    // See `resolveInitialLiveConnectRequested` for the full contract +
    // rationale. Pure helper so the auto-open-vs-gesture decision is
    // unit-testable without React.
    const initialLiveConnectRequested = resolveInitialLiveConnectRequested({
      autoOpenChannel,
      requestedLiveSessionKind
    });

    const {
      avatarHealth,
      browserAccessPrompt,
      clearLiveAutoListenTimer,
      clearLivePlaybackCountdown,
      clearLiveSpeakingTimeout,
      clearLiveVoiceRetryTimer,
      currentSession,
      dispatchAvatarEvent,
      ensureVoiceBrowserAccess,
      handleLivePromptOptionSelect,
      isAvatarReady,
      isInVoiceCall,
      isVoiceInitialized,
      liveAssistantText,
      liveChatEventId,
      liveChatText,
      liveConnectRequested,
      liveConnectionError,
      liveModeActive,
      livePromptFields,
      livePromptOptions,
      liveSessionContext,
      liveSessionState,
      liveTranscriptEventId,
      liveUserTranscript,
      liveUserTranscriptFinal,
      liveVoiceCallEnabled,
      markAvatarReady,
      refs: {
        liveAutoListenArmedRef,
        liveAutoListenPendingRef,
        liveAutoListenTimerRef,
        liveClientRef,
        liveManualHangupRef,
        livePromptAudioRequestRef,
        livePromptListeningRef,
        livePromptTextRef,
        liveSessionContextRef,
        liveSpeakingRef,
        liveThinkingRef,
        liveVoiceAttemptRef,
        liveVoicePromptStartedRef,
        liveVoiceRetryCountRef
      },
      requestLiveSession,
      retryBrowserAccess,
      setIsInVoiceCall,
      setLiveAssistantText,
      setLiveConnectRequested,
      setLiveConnectionError,
      setLiveStatus,
      setLiveTranscriptEventId,
      setLiveUserTranscript,
      setLiveUserTranscriptFinal,
      setPromptPlaybackUnlocked,
      sharedLiveAvatarConfig,
      stopLiveSyntheticAudioSync
    } = useHostedLiveController({
      activeListen,
      holdSpeechUntilUnlock,
      avatarInstanceId: effectiveAvatarInstanceId,
      avatarRef,
      builderDiagramId,
      cameraZoom: config.cameraZoom,
      canvasContainerRef,
      handoffTokenCamlin,
      handleVoiceEvent: handleEvent,
      initialLiveConnectRequested,
      initialSessionContext: sessionContext,
      isCallActiveRef,
      isEmbeddedBuilderView: false,
      isMobileCall,
      isVisualOnly,
      isWebrtcCall,
      liveDestination: config.liveDestination,
      liveModeRequested: true,
      noSip,
      projectTokenCamlin,
      queryParams,
      requestedLiveSessionKind,
      requestedLiveVoiceId,
      resolveLinkedAvatarModelId,
      resolvedTtsBaseUrl,
      runtimeUrl: config.runtimeUrl,
      selectedModel,
      setAnimateCallSpeak,
      setAudioSource,
      setDisplayText,
      setIsCallActive,
      setIsMobileCall,
      setIsWebrtcCall,
      setSpeak,
      showToast,
      sipWebSocketUrl: config.sipWebSocketUrl,
      stopAudioPlayback,
      unlockAudio,
      verboseAvatarDebug: false,
      voiceUrl: config.voiceUrl
    });

    const nativeAvatarConfig = useMemo<CamlinAvatarConfig>(() => ({
      ...sharedLiveAvatarConfig,
      diagramId: (liveSessionContext?.diagramId ?? builderDiagramId) || undefined,
      handoffToken: (liveSessionContext?.handoffToken ?? handoffTokenCamlin) || undefined,
      projectToken: (liveSessionContext?.projectToken ?? projectTokenCamlin) || undefined,
      // Branded loading splash: lets the underlying avatar resolve the
      // captured portrait through runtime-api while the model loads.
      runtimeUrl,
      ttsUrl: config.ttsUrl,
      initialAppearance,
      initialConfig,
      avatarInstanceId: effectiveAvatarInstanceId,
      ...(preloadModel === undefined ? {} : { preloadModel })
    }), [
      builderDiagramId,
      config.ttsUrl,
      effectiveAvatarInstanceId,
      handoffTokenCamlin,
      initialConfig,
      initialAppearance,
      liveSessionContext?.diagramId,
      liveSessionContext?.handoffToken,
      liveSessionContext?.projectToken,
      preloadModel,
      projectTokenCamlin,
      runtimeUrl,
      sharedLiveAvatarConfig
    ]);

    const { isMuted, toggleMute } = useVoiceMuteControl({
      currentSession,
      isMobileCall,
      isWebrtcCall,
      socketHandlerRef
    });

    const {
      handleLivePrimaryButtonClick,
      liveControlCaption,
      liveListenButtonActive,
      liveMuteButtonEnabled,
      livePrimaryButtonText,
      livePrimaryControlBusy,
      liveStatusLabel,
      liveVoiceSessionActive
    } = useLiveListenControls({
      refs: {
        liveAutoListenArmedRef,
        liveAutoListenPendingRef,
        liveAutoListenTimerRef,
        liveClientRef,
        liveManualHangupRef,
        livePromptAudioRequestRef,
        livePromptListeningRef,
        livePromptTextRef,
        liveSessionContextRef,
        liveSpeakingRef,
        liveThinkingRef,
        liveVoiceAttemptRef,
        liveVoicePromptStartedRef,
        liveVoiceRetryCountRef
      },
      activeListen,
      avatarRef,
      currentSession,
      clearLiveAutoListenTimer,
      clearLivePlaybackCountdown,
      clearLiveSpeakingTimeout,
      clearLiveVoiceRetryTimer,
      dispatchAvatarEvent,
      ensureVoiceBrowserAccess: (requiresMicrophone = false) => ensureVoiceBrowserAccess(requiresMicrophone),
      hangupDebounceTime: 1000,
      isAvatarReady,
      isCallActive,
      isInVoiceCall,
      isMobileCall,
      isVisualOnly: Boolean(isVisualOnly),
      isVoiceInitialized,
      isWebrtcCall,
      lastHangupTime,
      liveConnectRequested,
      liveConnectionError,
      liveModeActive,
      liveSessionStatus: liveSessionState?.status,
      liveVoiceCallEnabled,
      playing,
      record,
      refreshUIButtonsOnCallEnd: () => {},
      requestLiveSession,
      setDisplayText,
      setIsCallActive,
      setIsInVoiceCall,
      setIsMobileCall,
      setIsWebrtcCall,
      setLastHangupTime,
      setLiveAssistantText,
      setLiveConnectRequested,
      setLiveConnectionError,
      setLiveStatus,
      setLiveTranscriptEventId,
      setLiveUserTranscript,
      setLiveUserTranscriptFinal,
      setPromptPlaybackUnlocked,
      setRecord,
      setSpeak,
      setUserTalking,
      socketHandlerRef,
      stopAudioPlayback,
      stopLiveSyntheticAudioSync,
      handleInputExternally
    });

    const {
      liveEmbedCaption,
      liveInlineAssistantText,
      shouldShowVoiceOverlay,
      suppressEmbeddedLiveCopy
    } = useLiveOverlayState({
      avatarLoadingMessage: null,
      isEmbeddedBuilderView: false,
      isEmbeddedView: false,
      isMobileCall,
      isWebrtcCall,
      liveAssistantText,
      liveChatEventId,
      liveChatText,
      liveConnectionError,
      liveControlCaption,
      liveModeActive,
      livePrimaryButtonText,
      livePrimaryControlBusy,
      livePromptFieldCount: livePromptFields.length,
      livePromptOptions,
      livePromptTextRef,
      liveSessionStatus: liveSessionState?.status,
      liveTranscriptEventId,
      liveUserTranscript,
      liveUserTranscriptFinal,
      liveVoiceSessionActive,
      suppressEmbeddedLiveCopyOverride
    });

    const livePrimaryButtonTitle = liveVoiceSessionActive
      ? 'Stop listening and end the live call'
      : !isAvatarReady
        ? 'Please wait for the avatar to finish loading'
        : liveVoiceCallEnabled && !isVoiceInitialized
          ? 'Please wait for voice features to initialize'
          : 'Start listening';

    useEffect(() => {
      if (!isAvatarReady || readyNotifiedRef.current) {
        return;
      }

      readyNotifiedRef.current = true;
      onReady?.();
    }, [isAvatarReady, onReady]);

    useEffect(() => {
      if (!onSpeechInput || !liveUserTranscriptFinal || liveTranscriptEventId === 0) {
        return;
      }
      if (lastTranscriptEventIdRef.current === liveTranscriptEventId) {
        return;
      }

      lastTranscriptEventIdRef.current = liveTranscriptEventId;
      onSpeechInput(liveUserTranscript);
    }, [
      liveTranscriptEventId,
      liveUserTranscript,
      liveUserTranscriptFinal,
      onSpeechInput
    ]);

    // Mirror the browser-access prompt state to hosts that render their
    // own permission UI (see hideAudioPermissionChip). Fired on every
    // change including back to null (prompt satisfied/cleared).
    useEffect(() => {
      onBrowserAccessPromptChange?.(
        browserAccessPrompt
          ? {
              kind: browserAccessPrompt.kind,
              title: browserAccessPrompt.title,
              message: browserAccessPrompt.message
            }
          : null
      );
    }, [browserAccessPrompt, onBrowserAccessPromptChange]);

    useEffect(() => {
      if (!liveConnectionError || !onError || lastErrorRef.current === liveConnectionError) {
        return;
      }

      lastErrorRef.current = liveConnectionError;
      onError(new Error(liveConnectionError));
    }, [liveConnectionError, onError]);

    const handlePrimaryAction = useCallback(async () => {
      onMicClick?.();
      await handleLivePrimaryButtonClick();
    }, [handleLivePrimaryButtonClick, onMicClick]);

    useImperativeHandle(ref, () => ({
      speak: (text, options) => {
        // Voice-mismatch regression, 7 Aug 2026: imperative speak() calls that
        // omit voiceId (e.g. the Companion's first-open greeting replay) used
        // to POST /talk with no voice, and the TTS engine's service default is
        // en-AU-NatashaNeural — a female voice on the male Arc model. Pin the
        // persona's resolved voice here so EVERY speak carries an explicit
        // voice id; an explicit options.voiceId still wins.
        avatarRef.current?.speak(text, {
          ...options,
          voiceId: options?.voiceId || selectedModel.voiceId
        });
      },
      stop: () => {
        stopAudioPlayback();
        avatarRef.current?.stop();
      },
      setMood: (mood, intensity) => {
        avatarRef.current?.setMood(mood, intensity);
      },
      setListening: (listening) => {
        avatarRef.current?.setListening(listening);
        avatarRef.current?.setIsListening?.(listening);
      },
      setThinking: (thinking) => {
        avatarRef.current?.setThinking(thinking);
        avatarRef.current?.setIsThinking?.(thinking);
      },
      setIsListening: (listening) => {
        avatarRef.current?.setListening(listening);
        avatarRef.current?.setIsListening?.(listening);
      },
      setIsThinking: (thinking) => {
        avatarRef.current?.setThinking(thinking);
        avatarRef.current?.setIsThinking?.(thinking);
      },
      playBlendshapes: (frames) => {
        avatarRef.current?.playBlendshapes(frames);
      },
      playIdleAnimation: () => {
        avatarRef.current?.playIdleAnimation?.();
      },
      stopAllAnimations: () => {
        avatarRef.current?.stopAllAnimations?.();
      },
      stopVisemeAnimation: () => {
        avatarRef.current?.stopVisemeAnimation?.();
      },
      getMixer: () => avatarRef.current?.getMixer?.() ?? null,
      getMorphTargetDictionary: () => avatarRef.current?.getMorphTargetDictionary?.() ?? null,
      getState: () => avatarRef.current?.getState?.() ?? 'idle',
      isReady: () => avatarRef.current?.isReady?.() ?? isAvatarReady,
      // Camlin Companion (doc 116 CO1) drives its own text transcript/input
      // instead of the SDK's built-in chat UI — this forwards straight to
      // the LiveModeClient's `visual.input`, the same path
      // `useHostedLiveControllerActions.handleLivePromptOptionSelect` uses
      // for tapped prompt options.
      sendText: (value: string, metadata?: Record<string, unknown>) => {
        liveClientRef.current?.sendInput?.(value, metadata);
      },
      // Liveness probe for host send paths (Companion v8 item 2): lets the
      // host detect a stale session ON the user's action instead of after
      // a 45s reply stall.
      isLiveLinked: () => liveClientRef.current?.isLinked?.() ?? false,
      // Explicit audio unlock for hosts with their own "Enable sound"
      // affordance (Camlin Companion). Marks prompt playback unlocked AND
      // primes the actual audio elements — both halves matter: the ref
      // gate alone still leaves the <audio> element locked by autoplay
      // policy, and priming alone leaves activeListen surfaces suppressed.
      unlockAudio: async () => {
        setPromptPlaybackUnlocked(true);
        await unlockAudio();
      },
      // The canonical live-mode mic action (same as the LiveControlDock
      // primary button) for hosts that hide the dock.
      startVoiceTurn: async () => {
        await handlePrimaryAction();
      },
      retryBrowserAccess: async () => {
        await retryBrowserAccess();
      }
    }), [
      handlePrimaryAction,
      isAvatarReady,
      liveClientRef,
      retryBrowserAccess,
      selectedModel.voiceId,
      setPromptPlaybackUnlocked,
      stopAudioPlayback,
      unlockAudio
    ]);

    return (
      <>
        <div
          ref={canvasContainerRef}
          className={className}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            ...style
          }}
        >
          {shouldShowVoiceOverlay && (
            <LiveVoiceOverlay
              bottomOffsetPx={92}
              liveConnectionError={liveConnectionError}
              liveInlineAssistantText={liveInlineAssistantText}
              livePromptFields={livePromptFields}
              livePromptOptions={livePromptOptions}
              placement="bottom"
              liveSessionStatus={liveSessionState?.status}
              liveStatusLabel={liveStatusLabel}
              liveUserTranscript={liveUserTranscript}
              liveUserTranscriptFinal={liveUserTranscriptFinal}
              onFieldSubmit={(values) => {
                const combined = Object.values(values).filter(Boolean).join(', ');
                if (combined) {
                  handleLivePromptOptionSelect(combined, 'form-submit');
                }
              }}
              onPromptOptionSelect={handleLivePromptOptionSelect}
              suppressEmbeddedLiveCopy={suppressEmbeddedLiveCopy}
            />
          )}

          {/*
            Inline permission chip — replaces the page-blocking
            BrowserAccessPromptModal as the PRIMARY way to surface
            audio/microphone permission state when the avatar is
            embedded in a chat sidebar (Keith report 2026-04-23: "audio
            permission issue keeps occurring in the panel"). The modal
            is kept below as a fallback for accessibility and for the
            standalone runtime-host where there's no chat panel.
          */}
          {!hideAudioPermissionChip && (
            <AudioPermissionChip
              prompt={browserAccessPrompt}
              onGrant={retryBrowserAccess}
            />
          )}

          {activeListen && liveModeActive && !hideButtons && (
            <LiveControlDock
              caption={liveEmbedCaption}
              isMuted={isMuted}
              liveListenButtonActive={liveListenButtonActive}
              liveMuteButtonEnabled={liveMuteButtonEnabled}
              livePrimaryButtonText={livePrimaryButtonText}
              livePrimaryControlBusy={livePrimaryControlBusy}
              liveVoiceSessionActive={liveVoiceSessionActive}
              onPrimaryAction={handlePrimaryAction}
              onPrimaryPointerDown={() => { void unlockAudio(); }}
              onToggleMute={toggleMute}
              primaryButtonTitle={livePrimaryButtonTitle}
            />
          )}

          <AvatarAudioLayer
            audioPlayer={audioPlayer}
            audioSource={audioSource}
            avatarRef={avatarRef}
            setAudioSource={setAudioSource}
            setPlaying={setPlaying}
            setRecord={setRecord}
            setSpeak={setSpeak}
            onSpeakingEnd={onSpeakingEnd}
            onSpeakingStart={onSpeakingStart}
          />

          {/*
            Always-on status pip — same trap-point catalogue as the
            runtime host App.tsx. See useAvatarHealth.ts. Kept in the
            top-right corner; embedded surfaces that render their own
            health/connection UI opt out via `hideHealthBadge`.
          */}
          {!hideHealthBadge && <AvatarHealthBadge state={avatarHealth} position="top-right" />}

          <CamlinAvatar
            ref={avatarRef}
            config={nativeAvatarConfig}
            onAppearanceApplied={onAppearanceApplied}
            onError={onError}
            onMicClick={onMicClick}
            onMoodChange={(mood) => onMoodChange?.(mood)}
            onReady={markAvatarReady}
            onSpeakComplete={onSpeakingEnd}
            onSpeakStart={onSpeakingStart}
            style={{ position: 'absolute', inset: 0 }}
          />
        </div>
      </>
    );
  }
);

export default NativeLiveAvatar;
